// SavedCommercialViewer.js
import axios from '../../axios';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import TableResizer from './TableResizer';

const SavedCommercialViewer = () => {
  const { commercialId } = useParams();
  const [html, setHtml] = useState(undefined);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);
  const [layoutResetKey, setLayoutResetKey] = useState(0);

  useEffect(() => {
    axios
      .get(`${window.backendUrl}/get_commercial_saved_document/${commercialId}`)
      .then((res) => setHtml(res.data.htmlContent))
      .catch(() => setHtml(null));
  }, [commercialId]);

  useEffect(() => {
    if (!containerRef.current) return;

    const targetNodes = containerRef.current.querySelectorAll(
      'td, th, h1, span, [contenteditable]'
    );

    targetNodes.forEach((el) => {
      // Documents saved from the live page carry contenteditable="false" on every field
      // that page allowed editing. Those must always be flipped, even when they contain
      // <br>/<span> children (addresses, measurements, PI NO): left as "false" inside an
      // editable <td>, the browser treats them as locked islands you cannot type into.
      if (
        el.hasAttribute('contenteditable') ||
        el.children.length === 0 ||
        el.tagName === 'TD' ||
        el.tagName === 'TH'
      ) {
        el.setAttribute('contenteditable', editMode ? 'true' : 'false');
      }
    });
  }, [editMode, html]);

  const handleSave = async () => {
    if (!containerRef.current) return;
    setSaving(true);

    const editableElements = containerRef.current.querySelectorAll('[contenteditable]');
    editableElements.forEach((el) => el.removeAttribute('contenteditable'));

    const updatedHtml = containerRef.current.innerHTML;

    try {
      await axios.post(`${window.backendUrl}/save_commercial_document/${commercialId}`, {
        htmlContent: updatedHtml,
      });
      setEditMode(false);
    } catch (err) {
      console.error('Failed to update saved document:', err);
      editableElements.forEach((el) => el.setAttribute('contenteditable', 'true'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    if (containerRef.current && html) {
      containerRef.current.innerHTML = html;
    }
  };

  if (html === undefined) return null;
  if (html === null) return <p style={{ margin: 20 }}>No saved document found for this commercial.</p>;

  return (
    <div style={{ display: 'block', fontSize: '14px', margin: '0px 20px' }}>
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
            @bottom-right {
              content: counter(page);
              font-size: 12px;
              color: #444;
            }
          }
          .print-page-table {
            width: 100%;
            border-collapse: collapse;
            break-before: page;
            page-break-before: always;
          }
          .print-page-table:first-of-type {
            break-before: auto;
            page-break-before: auto;
          }
          .print-page-table thead {
            display: table-header-group; /* forces repeat on every printed page */
          }
          .header-cell {
            padding-bottom: 15px;
          }
          .no-print {
            display: none !important;
          }
          [contenteditable] {
            outline: none !important;
            background-color: transparent !important;
          }
        }
        .viewer-edit-mode [contenteditable="true"] {
          outline: 1px dashed #3b82f6 !important;
          background-color: rgba(59, 130, 246, 0.05);
          cursor: text;
          min-width: 12px;
        }
        .viewer-edit-mode [contenteditable="true"]:hover {
          background-color: rgba(59, 130, 246, 0.12);
        }
      `}</style>

      <div className="no-print" style={{ marginBottom: '10px' }}>
        <button onClick={() => (editMode ? handleCancel() : setEditMode(true))}>
          {editMode ? 'Normal View' : 'Edit Content'}
        </button>
        {editMode && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ marginLeft: '10px' }}
          >
            {saving ? 'Saving...' : 'Save Document'}
          </button>
        )}
        {editMode && (
          <button
            type="button"
            onClick={() => setLayoutResetKey((k) => k + 1)}
            style={{ marginLeft: '10px' }}
            title="Undo every column/row resize made to the tables since this page was opened"
          >
            Reset table layout
          </button>
        )}
      </div>

      {/* Wrapper only anchors the resize handles; they are outside containerRef so they are
          never part of the saved innerHTML. */}
      <div style={{ position: 'relative' }}>
        <div
          ref={containerRef}
          className={editMode ? 'viewer-edit-mode' : ''}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <TableResizer containerRef={containerRef} enabled={editMode} resetSignal={layoutResetKey} />
      </div>
    </div>
  );
};

export default SavedCommercialViewer;